import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock, FileText, PenTool, Upload, ChevronLeft, ChevronRight, Check,
  Sparkles, Rocket, Zap, Timer, Hourglass, RotateCcw, Image as ImageIcon, X,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [
  { mins: 60,  label: "1 hour",  blueprint: "20 MCQ · 8 Short · 1 Essay",  marks: 54 },
  { mins: 120, label: "2 hours", blueprint: "35 MCQ · 15 Short · 2 Essays", marks: 100 },
  { mins: 180, label: "3 hours", blueprint: "50 MCQ · 20 Short · 3 Essays", marks: 140 },
];

const PACE_TIERS = [
  { id: "lightning", label: "Lightning", maxAvg: 30,  color: "#FCA5A5", Icon: Rocket,    blurb: "Insanely fast — verify accuracy on tricky topics." },
  { id: "quick",     label: "Quick",     maxAvg: 60,  color: "#5AC8FA", Icon: Zap,       blurb: "Sharp exam pace." },
  { id: "steady",    label: "Steady",    maxAvg: 120, color: "#B8A4FF", Icon: Timer,     blurb: "Measured — good for hard sections." },
  { id: "thorough",  label: "Thorough",  maxAvg: Infinity, color: "#7C5CFF", Icon: Hourglass, blurb: "Careful pacing — try the timed retry." },
];

function paceTierFor(avgSec) {
  return PACE_TIERS.find((t) => avgSec < t.maxAvg) || PACE_TIERS[PACE_TIERS.length - 1];
}
function pad(n) { return String(n).padStart(2, "0"); }
function fmtHMS(s) {
  s = Math.max(0, Math.round(s));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}
function fmtMS(s) {
  s = Math.max(0, Math.round(s));
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

export default function ExamPanel({ scId, planTier, proPass }) {
  const [view, setView] = useState("setup"); // setup | running | uploading | result
  const [exam, setExam] = useState(null);
  const [result, setResult] = useState(null);

  const [resumeId, setResumeId] = useState(null);

  const isPro = planTier === "pro" || (proPass && new Date(proPass).getTime() > Date.now());

  // Resume an exam that is still being built or still running for this study.
  useEffect(() => {
    if (!isPro) return;
    api.get("/exams/mine/list").then(async ({ data }) => {
      const ex = (data.exams || []).find((e) => e.sc_id === scId && (e.status === "generating"
        || (e.status === "in_progress" && new Date(e.ends_at).getTime() > Date.now())));
      if (!ex) return;
      if (ex.status === "generating") { setResumeId(ex.id); return; }
      const { data: full } = await api.get(`/exams/${ex.id}`);
      setExam(full);
      setView(full.mode === "handwritten" ? "uploading" : "running");
      toast("Resumed your exam in progress");
    }).catch(() => {});
  }, [scId, isPro]);

  if (!isPro) return <ExamPaywall planTier={planTier} />;

  if (view === "setup") {
    return <ExamSetup scId={scId} resumeId={resumeId} onStarted={(ex) => { setExam(ex); setView(ex.mode === "handwritten" ? "uploading" : "running"); }} />;
  }
  if (view === "running" && exam) {
    return (
      <ExamRunner
        exam={exam}
        onSubmitted={(res) => { setResult(res); setView("result"); }}
        onCancel={() => setView("setup")}
      />
    );
  }
  if (view === "uploading" && exam) {
    return (
      <ExamHandwrittenUpload
        exam={exam}
        onSubmitted={(res) => { setResult(res); setView("result"); }}
        onCancel={() => setView("setup")}
      />
    );
  }
  if (view === "result" && result) {
    return <ExamResult result={result} exam={exam} onRetry={() => { setExam(null); setResult(null); setView("setup"); }} />;
  }
  return null;
}

/* ----------------------------- SETUP ----------------------------- */
function ExamSetup({ scId, resumeId, onStarted }) {
  const [duration, setDuration] = useState(60);
  const [mode, setMode] = useState("typed");
  const [busy, setBusy] = useState(false);

  // The exam keeps building on the server if you navigate away; we pick it back up here.
  useEffect(() => {
    if (!resumeId) return;
    start(resumeId);
    // eslint-disable-next-line
  }, [resumeId]);

  const start = async (existingId) => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading(existingId ? "Resuming your exam build…" : "Building your exam… usually 10-20s");
    try {
      let examId = typeof existingId === "string" ? existingId : null;
      if (!examId) {
        const { data: pending } = await api.post("/exams/start",
          { sc_id: scId, duration_minutes: duration, mode },
          { timeout: 30000 });
        examId = pending.id;
      }

      // Poll until backend flips status to in_progress (or failed).
      const deadline = Date.now() + 180000;
      let exam = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: ex } = await api.get(`/exams/${examId}`, { timeout: 15000 });
        if (ex.status === "in_progress") { exam = ex; break; }
        if (ex.status === "failed") {
          throw new Error(ex.error || "Generation failed");
        }
      }
      if (!exam) throw new Error("Generation timed out — please retry");
      toast.success("Exam ready. Good luck.", { id: t });
      onStarted(exam);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't start exam", { id: t });
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto" data-testid="exam-setup">
      <div className="surface rounded-2xl p-7 md:p-9 mb-6">
        <div className="eyebrow mb-3">EXAM MODE</div>
        <h2 className="display-3 mb-3">Sit a timed mock.</h2>
        <p className="text-[14px] text-[#B0AEBC] max-w-prose mb-7">
          AI-built exam on this study&apos;s material, graded the way an examiner would.
          Choose your duration and mode below — then hit Start. No going back once the timer begins.
        </p>

        {/* Duration picker */}
        <div className="eyebrow mb-3">DURATION</div>
        <div className="grid sm:grid-cols-3 gap-3 mb-7">
          {DURATIONS.map((d) => {
            const active = duration === d.mins;
            return (
              <button
                key={d.mins}
                onClick={() => setDuration(d.mins)}
                data-testid={`exam-duration-${d.mins}`}
                className="relative text-left p-4 rounded-2xl border transition-all"
                style={{
                  background: active ? "linear-gradient(135deg, rgba(124,92,255,0.18), transparent 70%)" : "rgba(255,255,255,0.02)",
                  borderColor: active ? "rgba(124,92,255,0.55)" : "rgba(255,255,255,0.06)",
                  boxShadow: active ? "0 0 24px -10px rgba(124,92,255,0.6), inset 0 0 0 1px rgba(124,92,255,0.3)" : "none",
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[16px] font-semibold">{d.label}</span>
                  <Clock size={14} className="text-[#B8A4FF]" />
                </div>
                <div className="text-[11.5px] text-[#8E8B99]">{d.blueprint}</div>
                <div className="text-[10.5px] text-[#71717A] mt-2 font-mono">/ {d.marks} marks</div>
              </button>
            );
          })}
        </div>

        {/* Mode picker */}
        <div className="eyebrow mb-3">MODE</div>
        <div className="grid sm:grid-cols-2 gap-3 mb-7">
          <ModeCard
            value="typed" active={mode === "typed"} onPick={() => setMode("typed")}
            Icon={FileText} title="Typed"
            blurb="Best for revision speed. Type your answers, get instant grading."
            testid="exam-mode-typed"
          />
          <ModeCard
            value="handwritten" active={mode === "handwritten"} onPick={() => setMode("handwritten")}
            Icon={PenTool} title="Handwritten"
            blurb="Sit it on paper, upload one photo of your sheet. AI vision reads & grades it."
            testid="exam-mode-handwritten"
            badge="NEW"
          />
        </div>

        <div className="bg-[#FCA5A5]/[0.04] border border-[#FCA5A5]/15 rounded-xl p-3.5 mb-7 flex items-start gap-2.5">
          <AlertTriangle size={14} className="text-[#FCA5A5] mt-0.5 shrink-0" />
          <div className="text-[12.5px] text-[#E5E5E7] leading-relaxed">
            <strong className="text-white">Heads up.</strong> Once you start, the timer can&apos;t be paused.
            If you close the tab the exam auto-submits when the timer hits zero.
          </div>
        </div>

        <button
          onClick={start}
          disabled={busy}
          data-testid="exam-start-btn"
          className="btn btn-primary w-full py-3.5 text-[14px] disabled:opacity-60"
        >
          {busy ? "Building…" : (<>Start exam <Sparkles size={14} /></>)}
        </button>
      </div>
    </motion.div>
  );
}

function ModeCard({ Icon, title, blurb, active, onPick, testid, badge }) {
  return (
    <button
      onClick={onPick}
      data-testid={testid}
      className="relative text-left p-4 rounded-2xl border transition-all"
      style={{
        background: active ? "linear-gradient(135deg, rgba(184,164,255,0.16), transparent 70%)" : "rgba(255,255,255,0.02)",
        borderColor: active ? "rgba(184,164,255,0.55)" : "rgba(255,255,255,0.06)",
        boxShadow: active ? "0 0 24px -10px rgba(184,164,255,0.55), inset 0 0 0 1px rgba(184,164,255,0.3)" : "none",
      }}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-[#B8A4FF]"><Icon size={16} /></div>
        <div className="text-[15px] font-semibold">{title}</div>
        {badge && <span className="ml-auto text-[9.5px] font-bold tracking-[0.18em] uppercase text-[#FCA5A5] bg-[#FCA5A5]/10 border border-[#FCA5A5]/30 px-2 py-0.5 rounded-full">{badge}</span>}
      </div>
      <p className="text-[12.5px] text-[#8E8B99] leading-snug">{blurb}</p>
    </button>
  );
}

/* ----------------------------- RUNNER ----------------------------- */
function ExamRunner({ exam, onSubmitted, onCancel }) {
  // Build flat question list with metadata.
  const flat = [
    ...exam.mcq.map((q, i) => ({ kind: "mcq", i, q })),
    ...exam.short.map((q, i) => ({ kind: "short", i, q })),
    ...exam.essay.map((q, i) => ({ kind: "essay", i, q })),
  ];

  const startMsRef = useRef(new Date(exam.started_at).getTime());
  const endMsRef = useRef(new Date(exam.ends_at).getTime());
  const [now, setNow] = useState(Date.now());
  const [idx, setIdx] = useState(0);
  const saveKey = `sv_exam_answers_${exam.id}`;
  const saved = (() => { try { return JSON.parse(localStorage.getItem(saveKey) || "null"); } catch { return null; } })();
  const [mcq, setMcq] = useState(() => saved?.mcq || Array(exam.mcq.length).fill(null));
  const [short, setShort] = useState(() => saved?.short || Array(exam.short.length).fill(""));
  const [essay, setEssay] = useState(() => saved?.essay || Array(exam.essay.length).fill(""));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(saveKey, JSON.stringify({ mcq, short, essay })); } catch {}
  }, [saveKey, mcq, short, essay]);

  const remaining = Math.max(0, (endMsRef.current - now) / 1000);
  const elapsed = (now - startMsRef.current) / 1000;
  const expired = remaining <= 0;

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const submit = async (autoExpired = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/exams/${exam.id}/submit`,
        { mcq_answers: mcq, short_answers: short, essay_answers: essay,
          elapsed_seconds: Math.round((Date.now() - startMsRef.current) / 1000) },
        { timeout: 180000 });
      try { localStorage.removeItem(saveKey); } catch {}
      onSubmitted({ ...data.result, xp_awarded: data.xp_awarded, autoExpired });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
      setSubmitting(false);
    }
  };

  // Auto-submit when timer hits 0.
  useEffect(() => {
    if (expired && !submitting) submit(true);
    // eslint-disable-next-line
  }, [expired]);

  const current = flat[idx];
  const answeredCount =
    mcq.filter((x) => x).length + short.filter((x) => x.trim()).length + essay.filter((x) => x.trim()).length;

  const timeWarn = remaining < 300; // last 5 min
  const timeCrit = remaining < 60;  // last minute

  return (
    <div className="max-w-4xl mx-auto" data-testid="exam-runner">
      {/* Sticky top bar */}
      <div
        className="sticky top-[100px] z-20 mb-6 surface rounded-2xl px-5 py-4 flex items-center gap-4 backdrop-blur-md"
        style={{
          boxShadow: timeCrit ? "0 0 28px -8px rgba(252,165,165,0.55)" : timeWarn ? "0 0 22px -8px rgba(252,165,165,0.30)" : "0 0 22px -10px rgba(124,92,255,0.4)",
          borderColor: timeCrit ? "rgba(252,165,165,0.45)" : undefined,
        }}
      >
        <ExamTimerRing seconds={remaining} total={exam.duration_minutes * 60} critical={timeCrit} warn={timeWarn} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] uppercase tracking-[0.16em] text-[#8E8B99] font-bold">Time remaining</div>
          <div
            className="text-[24px] font-bold tabular-nums leading-none mt-0.5"
            style={{ color: timeCrit ? "#FCA5A5" : timeWarn ? "#FBBF24" : "#FFFFFF" }}
            data-testid="exam-time-remaining"
          >
            {fmtHMS(remaining)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#8E8B99] font-bold">Answered</div>
          <div className="text-[18px] font-semibold tabular-nums">{answeredCount} / {flat.length}</div>
        </div>
        <button
          onClick={() => {
            if (window.confirm("Submit now? Unanswered questions will count as zero.")) submit(false);
          }}
          disabled={submitting}
          data-testid="exam-submit-btn"
          className="btn btn-primary px-5 py-2 text-[13px] disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </div>

      {/* Question palette */}
      <QuestionPalette flat={flat} idx={idx} setIdx={setIdx} mcq={mcq} short={short} essay={essay} />

      {/* Question card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${current.kind}-${current.i}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          className="surface rounded-2xl p-6 md:p-7 mb-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8E8B99]">
              {current.kind === "mcq" ? `MCQ ${current.i + 1}` : current.kind === "short" ? `Short ${current.i + 1}` : `Essay ${current.i + 1}`}
            </div>
            <div className="text-[11px] text-[#71717A] font-mono">{current.q.marks} mark{current.q.marks > 1 ? "s" : ""}</div>
          </div>

          <h3 className="text-[16px] md:text-[17.5px] font-semibold mb-5 leading-snug">{current.q.question}</h3>

          {current.kind === "mcq" && (
            <div className="space-y-2.5">
              {(current.q.options || []).map((opt, i) => {
                const letter = String.fromCharCode(65 + i);
                const picked = mcq[current.i] === letter;
                return (
                  <button
                    key={letter}
                    onClick={() => setMcq((arr) => arr.map((v, j) => j === current.i ? letter : v))}
                    data-testid={`exam-mcq-${current.i}-${letter}`}
                    className="w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center gap-3"
                    style={{
                      background: picked ? "linear-gradient(135deg, rgba(124,92,255,0.20), transparent 70%)" : "rgba(255,255,255,0.02)",
                      borderColor: picked ? "rgba(124,92,255,0.55)" : "rgba(255,255,255,0.06)",
                      boxShadow: picked ? "inset 0 0 0 1px rgba(124,92,255,0.4)" : "none",
                    }}
                  >
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-bold shrink-0"
                      style={{
                        background: picked ? "#7C5CFF" : "rgba(255,255,255,0.06)",
                        color: picked ? "white" : "#B0AEBC",
                      }}
                    >
                      {letter}
                    </div>
                    <span className="text-[14px] flex-1">{opt}</span>
                    {picked && <Check size={14} className="text-[#B8A4FF]" />}
                  </button>
                );
              })}
            </div>
          )}

          {current.kind === "short" && (
            <textarea
              value={short[current.i] || ""}
              onChange={(e) => setShort((arr) => arr.map((v, j) => j === current.i ? e.target.value : v))}
              data-testid={`exam-short-${current.i}`}
              rows={5}
              placeholder="Type your answer…"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] leading-[1.6] focus:outline-none focus:border-[#7C5CFF] transition-colors resize-y"
            />
          )}

          {current.kind === "essay" && (
            <textarea
              value={essay[current.i] || ""}
              onChange={(e) => setEssay((arr) => arr.map((v, j) => j === current.i ? e.target.value : v))}
              data-testid={`exam-essay-${current.i}`}
              rows={12}
              placeholder="Structure: introduction, 3-4 paragraphs of argument, conclusion."
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] leading-[1.7] focus:outline-none focus:border-[#7C5CFF] transition-colors resize-y"
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          data-testid="exam-prev-btn"
          className="btn btn-ghost text-[13px] disabled:opacity-40"
        >
          <ChevronLeft size={14} /> Previous
        </button>
        <span className="text-[12px] text-[#8E8B99]">Question {idx + 1} of {flat.length}</span>
        <button
          onClick={() => setIdx((i) => Math.min(flat.length - 1, i + 1))}
          disabled={idx === flat.length - 1}
          data-testid="exam-next-btn"
          className="btn btn-ghost text-[13px] disabled:opacity-40"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>

      {/* Subtle elapsed indicator */}
      <div className="text-center mt-6 text-[11px] text-[#71717A] tabular-nums">
        Elapsed {fmtHMS(elapsed)}
      </div>
    </div>
  );
}

function QuestionPalette({ flat, idx, setIdx, mcq, short, essay }) {
  return (
    <div className="surface rounded-2xl p-3 mb-5">
      <div className="flex flex-wrap gap-1.5">
        {flat.map((it, i) => {
          const answered =
            (it.kind === "mcq"   && mcq[it.i]) ||
            (it.kind === "short" && (short[it.i] || "").trim()) ||
            (it.kind === "essay" && (essay[it.i] || "").trim());
          const active = i === idx;
          return (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`exam-palette-${i}`}
              className="w-7 h-7 rounded-md text-[11px] font-semibold transition-all tabular-nums"
              style={{
                background: active ? "linear-gradient(135deg, #7C5CFF, #5AC8FA)" : answered ? "rgba(124,92,255,0.15)" : "rgba(255,255,255,0.04)",
                color: active ? "white" : answered ? "#E5E5E7" : "#8E8B99",
                border: `1px solid ${active ? "rgba(124,92,255,0.6)" : answered ? "rgba(124,92,255,0.30)" : "rgba(255,255,255,0.06)"}`,
                boxShadow: active ? "0 0 18px -4px rgba(124,92,255,0.6)" : "none",
              }}
              title={`${it.kind.toUpperCase()} ${it.i + 1}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ExamTimerRing({ seconds, total, critical, warn }) {
  const pct = Math.max(0, Math.min(1, seconds / Math.max(1, total)));
  const circ = 2 * Math.PI * 22;
  const color = critical ? "#FCA5A5" : warn ? "#FBBF24" : "#B8A4FF";
  return (
    <div className="relative w-14 h-14 shrink-0">
      <svg viewBox="0 0 50 50" className="w-full h-full -rotate-90">
        <circle cx="25" cy="25" r="22" stroke="rgba(255,255,255,0.06)" strokeWidth="3" fill="none" />
        <motion.circle
          cx="25" cy="25" r="22" stroke={color} strokeWidth="3" fill="none"
          strokeLinecap="round"
          strokeDasharray={circ}
          animate={{ strokeDashoffset: circ * (1 - pct) }}
          transition={{ duration: 0.3, ease: "linear" }}
          style={{ filter: `drop-shadow(0 0 6px ${color})` }}
        />
      </svg>
      <motion.div
        className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums"
        style={{ color }}
        animate={critical ? { scale: [1, 1.08, 1] } : {}}
        transition={critical ? { duration: 0.8, repeat: Infinity } : {}}
      >
        {Math.floor(seconds / 60)}m
      </motion.div>
    </div>
  );
}

/* ----------------------------- HANDWRITTEN UPLOAD ----------------------------- */
function ExamHandwrittenUpload({ exam, onSubmitted, onCancel }) {
  const startMs = useRef(new Date(exam.started_at).getTime());
  const endMs = useRef(new Date(exam.ends_at).getTime());
  const [now, setNow] = useState(Date.now());
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remaining = Math.max(0, (endMs.current - now) / 1000);
  const expired = remaining <= 0;

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      toast.error("That image is over 6 MB — try a smaller photo.");
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error("Use PNG, JPEG or WEBP.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    const t = toast.loading("Reading your handwriting + grading… ~30s");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("elapsed_seconds", String(Math.round((Date.now() - startMs.current) / 1000)));
      const { data } = await api.post(`/exams/${exam.id}/upload-sheet`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success("Graded!", { id: t });
      onSubmitted({ ...data.result, xp_awarded: data.xp_awarded });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't grade the sheet", { id: t });
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="exam-handwritten">
      {/* Header */}
      <div className="surface rounded-2xl p-5 mb-5 flex items-center gap-4">
        <ExamTimerRing seconds={remaining} total={exam.duration_minutes * 60} critical={remaining < 60} warn={remaining < 300} />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8E8B99]">Time remaining</div>
          <div className="text-[22px] font-bold tabular-nums">{fmtHMS(remaining)}</div>
        </div>
        <div className="text-right text-[11px] text-[#8E8B99]">
          <div>Mode: <span className="text-white font-semibold">Handwritten</span></div>
          <div>{exam.duration_minutes / 60}h exam · {exam.blueprint?.label}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Question paper */}
        <div className="surface rounded-2xl p-6 max-h-[560px] overflow-y-auto" data-testid="exam-question-paper">
          <div className="eyebrow mb-3">QUESTION PAPER</div>
          <div className="text-[12.5px] text-[#8E8B99] mb-4 leading-relaxed">
            Write your answers on paper. Label them clearly — e.g. <span className="text-white font-mono bg-white/5 px-1.5 py-0.5 rounded">Q1) B</span>{" "}or{" "}
            <span className="text-white font-mono bg-white/5 px-1.5 py-0.5 rounded">Short 2)</span>.
          </div>
          <QuestionPaper exam={exam} />
        </div>

        {/* Upload pane */}
        <div className="surface rounded-2xl p-6">
          <div className="eyebrow mb-3">UPLOAD ANSWER SHEET</div>
          <p className="text-[12.5px] text-[#8E8B99] mb-4 leading-relaxed">
            Take a clear, well-lit photo of your sheet. PNG/JPEG/WEBP, up to 6 MB. Tip:
            shoot top-down, no shadows.
          </p>

          <div
            onClick={() => !busy && !expired && fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
            data-testid="exam-upload-dropzone"
            className="relative aspect-[4/3] rounded-xl border-2 border-dashed border-white/10 hover:border-[#B8A4FF]/50 transition-colors flex items-center justify-center text-center cursor-pointer mb-4 overflow-hidden"
            style={busy || expired ? { opacity: 0.5, pointerEvents: "none" } : {}}
          >
            {preview ? (
              <>
                <img src={preview} alt="" className="absolute inset-0 w-full h-full object-contain bg-black/40" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white"
                  data-testid="exam-clear-preview"
                >
                  <X size={13} />
                </button>
              </>
            ) : (
              <div className="px-6">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-white/[0.05] flex items-center justify-center mb-3"><ImageIcon size={20} className="text-[#B8A4FF]" /></div>
                <div className="text-[14px] font-semibold mb-1">Click to upload, or drop a photo</div>
                <div className="text-[12px] text-[#8E8B99]">PNG · JPEG · WEBP · ≤ 6 MB</div>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              data-testid="exam-upload-input"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
          </div>

          <button
            onClick={upload}
            disabled={!file || busy || expired}
            data-testid="exam-grade-btn"
            className="btn btn-primary w-full py-3.5 text-[14px] disabled:opacity-40"
          >
            {busy ? "Grading…" : (<>Grade my sheet <Upload size={14} /></>)}
          </button>

          <div className="text-[11px] text-[#71717A] mt-3 text-center">
            Powered by Studivault AI vision — your image is only sent for grading and not retained.
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionPaper({ exam }) {
  return (
    <div className="space-y-6 text-[13px] leading-[1.65]">
      {exam.mcq.length > 0 && (
        <Section title="MCQ" hint="Write the letter only.">
          {exam.mcq.map((q, i) => (
            <article key={i} className="mb-4">
              <div className="font-semibold mb-1.5">Q{i + 1}. {q.question}</div>
              <ol className="pl-5 space-y-0.5 text-[#B0AEBC]" type="A">
                {(q.options || []).map((o, j) => (
                  <li key={j}>{String.fromCharCode(65 + j)}) {o}</li>
                ))}
              </ol>
            </article>
          ))}
        </Section>
      )}
      {exam.short.length > 0 && (
        <Section title="Short Answer" hint="2-4 sentences each.">
          {exam.short.map((q, i) => (
            <article key={i} className="mb-4">
              <div className="font-semibold mb-0.5">Short {i + 1}. {q.question}</div>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#71717A] font-mono">{q.marks} mark{q.marks > 1 ? "s" : ""}</div>
            </article>
          ))}
        </Section>
      )}
      {exam.essay.length > 0 && (
        <Section title="Essay">
          {exam.essay.map((q, i) => (
            <article key={i} className="mb-4">
              <div className="font-semibold mb-0.5">Essay {i + 1}. {q.question}</div>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#71717A] font-mono">{q.marks} marks</div>
            </article>
          ))}
        </Section>
      )}
    </div>
  );
}
function Section({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-2 mb-2">
        <h4 className="text-[14.5px] font-bold tracking-tight grad-text">{title}</h4>
        {hint && <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#71717A]">{hint}</span>}
      </div>
      <div className="pl-1">{children}</div>
    </section>
  );
}

/* ----------------------------- RESULT ----------------------------- */
function ExamResult({ result, exam, onRetry }) {
  const tier = paceTierFor(result.avg_seconds || 0);
  const PaceIcon = tier.Icon;
  const pct = result.percentage || 0;
  const isPerfect = pct >= 90;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="max-w-3xl mx-auto"
      data-testid="exam-result"
    >
      {/* Hero */}
      <div
        className={`surface rounded-3xl p-8 md:p-10 mb-6 relative overflow-hidden text-center ${isPerfect ? "border-[#B8A4FF]/40" : ""}`}
        style={{
          background: isPerfect
            ? "linear-gradient(135deg, rgba(124,92,255,0.32), rgba(90,200,250,0.20))"
            : "linear-gradient(135deg, rgba(184,164,255,0.12), transparent 70%)",
          boxShadow: isPerfect ? "0 0 60px -20px rgba(124,92,255,0.6), inset 0 0 0 1px rgba(184,164,255,0.25)" : undefined,
        }}
      >
        <div className="eyebrow mb-3 text-white/80">EXAM RESULT</div>

        {/* Score ring */}
        <ScoreRing pct={pct} />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="text-[13px] text-white/80 mt-2"
        >
          {result.earned} / {result.max} marks {result.mode === "handwritten" ? "· handwritten · graded by AI" : "· typed · graded by AI"}
        </motion.div>

        {result.xp_awarded != null && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, type: "spring", stiffness: 220 }}
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-white text-[13px] font-semibold"
          >
            <Sparkles size={13} /> +{result.xp_awarded} XP
          </motion.div>
        )}
      </div>

      {/* Pace badge */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85 }}
        className="surface rounded-2xl p-5 md:p-6 mb-6 flex items-center gap-5"
        style={{ boxShadow: `inset 0 0 0 1px ${tier.color}33, 0 0 26px -12px ${tier.color}66` }}
        data-testid="exam-pace-badge"
      >
        <PaceRing color={tier.color} avgSec={result.avg_seconds || 0} maxScale={150} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: tier.color }}>
            {tier.label} pace
          </div>
          <div className="text-[15px] font-semibold mb-1">{tier.blurb}</div>
          <div className="text-[12.5px] text-[#8E8B99] tabular-nums">
            {fmtHMS(result.elapsed_seconds)} total · {(result.avg_seconds || 0).toFixed(1)}s / question · {result.total_questions} Qs
          </div>
        </div>
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${tier.color}1A` }}>
          <PaceIcon size={18} style={{ color: tier.color }} />
        </div>
      </motion.div>

      {/* Per-question breakdown */}
      <div className="surface rounded-2xl p-5 md:p-6 mb-6" data-testid="exam-breakdown">
        <div className="eyebrow mb-4">PER-QUESTION FEEDBACK</div>
        <div className="space-y-3">
          {(result.mcq_results || []).map((r, i) => (
            <Row key={`m${i}`} title={`MCQ ${i + 1}`}
              marks={r.marks} max={r.max_marks}
              tone={r.correct ? "pass" : "fail"}
              note={r.correct ? "Correct." : `Picked ${r.given || "—"}. ${r.explanation || ""}`}
            />
          ))}
          {(result.written_results || []).map((r, i) => {
            const pctR = r.max_marks ? r.marks / r.max_marks : 0;
            const tone = pctR > 0.75 ? "pass" : pctR > 0.4 ? "warn" : "fail";
            return (
              <Row key={`w${i}`}
                title={`${r.kind === "essay" ? "Essay" : "Short"} ${r.id.split("_")[1] * 1 + 1}`}
                marks={r.marks} max={r.max_marks} tone={tone}
                note={r.feedback || "Graded."}
                excerpt={r.student_answer}
                status={r.status}
              />
            );
          })}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={onRetry} className="btn btn-ghost flex-1 py-3 text-[13.5px]" data-testid="exam-retry-btn">
          <RotateCcw size={13} /> Sit another mock
        </button>
      </div>
    </motion.div>
  );
}

function ScoreRing({ pct }) {
  const circ = 2 * Math.PI * 56;
  const color = pct >= 80 ? "#B8A4FF" : pct >= 60 ? "#5AC8FA" : pct >= 40 ? "#FBBF24" : "#FCA5A5";
  return (
    <div className="relative w-[160px] h-[160px] mx-auto">
      <svg viewBox="0 0 130 130" className="w-full h-full -rotate-90">
        <circle cx="65" cy="65" r="56" stroke="rgba(255,255,255,0.08)" strokeWidth="7" fill="none" />
        <motion.circle
          cx="65" cy="65" r="56" stroke={color} strokeWidth="7" fill="none"
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ * (1 - pct / 100) }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.15 }}
          style={{ filter: `drop-shadow(0 0 14px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-[42px] font-bold tabular-nums leading-none"
          style={{ color, textShadow: `0 0 22px ${color}55` }}
          data-testid="exam-pct"
        >
          {Math.round(pct)}%
        </motion.div>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/70 font-bold mt-1">SCORE</div>
      </div>
    </div>
  );
}

function PaceRing({ color, avgSec, maxScale = 150 }) {
  const pct = Math.min(1, avgSec / maxScale);
  const circ = 2 * Math.PI * 22;
  return (
    <div className="relative w-14 h-14 shrink-0">
      <svg viewBox="0 0 50 50" className="w-full h-full -rotate-90">
        <circle cx="25" cy="25" r="22" stroke="rgba(255,255,255,0.06)" strokeWidth="3" fill="none" />
        <motion.circle
          cx="25" cy="25" r="22" stroke={color} strokeWidth="3" fill="none"
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ * (1 - pct) }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.5 }}
          style={{ filter: `drop-shadow(0 0 8px ${color}88)` }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums" style={{ color }}>
        {fmtMS(avgSec)}
      </div>
    </div>
  );
}

function Row({ title, marks, max, tone, note, excerpt, status }) {
  const palette = tone === "pass"
    ? { bar: "#B8A4FF", chip: "rgba(184,164,255,0.18)", label: "text-[#B8A4FF]" }
    : tone === "warn"
    ? { bar: "#FBBF24", chip: "rgba(251,191,36,0.18)", label: "text-[#FBBF24]" }
    : { bar: "#FCA5A5", chip: "rgba(252,165,165,0.18)", label: "text-[#FCA5A5]" };

  const pct = max ? Math.min(1, marks / max) : 0;
  return (
    <div className="bg-white/[0.03] rounded-xl p-3.5 border border-white/[0.06]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] font-semibold">{title}</span>
          {status === "missing" && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#FCA5A5] bg-[#FCA5A5]/10 px-1.5 py-0.5 rounded">Missing</span>}
          {status === "unreadable" && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#FBBF24] bg-[#FBBF24]/10 px-1.5 py-0.5 rounded">Unreadable</span>}
        </div>
        <span className={`text-[12px] font-bold tabular-nums ${palette.label}`}>{marks.toFixed(1)} / {max}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-white/[0.05] mb-2">
        <motion.div
          className="h-full"
          style={{ background: palette.bar, boxShadow: `0 0 10px ${palette.bar}` }}
          initial={{ width: 0 }}
          animate={{ width: `${pct * 100}%` }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      </div>
      <div className="text-[12px] text-[#B0AEBC] leading-snug">{note}</div>
      {excerpt && (
        <div className="text-[11.5px] text-[#71717A] mt-1.5 italic line-clamp-2">&ldquo;{excerpt.slice(0, 220)}{excerpt.length > 220 ? "…" : ""}&rdquo;</div>
      )}
    </div>
  );
}

/* ----------------------------- PAYWALL ----------------------------- */
function ExamPaywall() {
  return (
    <div className="max-w-2xl mx-auto" data-testid="exam-paywall">
      <div className="surface rounded-2xl p-8 md:p-10 text-center"
           style={{ boxShadow: "0 0 60px -24px rgba(124,92,255,0.5)" }}>
        <div className="inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-5"
             style={{ background: "linear-gradient(135deg,#7C5CFF,#5AC8FA)", boxShadow: "0 0 38px -8px rgba(124,92,255,0.7)" }}>
          <FileText size={22} className="text-white" />
        </div>
        <h2 className="display-3 mb-3">Timed Exam Mode</h2>
        <p className="body-lg text-[#B0AEBC] mb-7 max-w-md mx-auto">
          AI-built 1h / 2h / 3h mock exams on this study&apos;s material — typed or upload a handwritten sheet for vision-based grading. Pro feature.
        </p>
        <a href="/app/billing">
          <button className="btn btn-primary px-6 py-3 text-[14px]" data-testid="exam-upgrade-btn">
            <Sparkles size={14} /> Upgrade to Pro
          </button>
        </a>
        <div className="mt-4 text-[12px] text-[#71717A]">Or redeem a 24h Pro Pass with 300 gems.</div>
      </div>
    </div>
  );
}
